import { api } from "@/lib/api";
import type {
  BlacklistEntry,
  CapacityDefault,
  FleetAvatar,
  FleetAvatarGroup,
  FleetAvatarReportedGroup,
  FleetStats,
  GroupDashboardResponse,
  GroupSessionsResponse,
  JoinCommand,
  SessionObjectsResponse,
  SessionsOverviewResponse,
  UnifiedGroupsResponse,
} from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ── Avatars ──
export function useFleetAvatars(filters?: {
  platform?: string;
  status?: string;
  search?: string;
  /**
   * IR-1327 r2: soft-deleted avatars are excluded server-side by default so this
   * tab counts the SAME population as the Monitoring Scrapers panel and the
   * Total card. Set only when the user picks the "Deleted" status chip.
   */
  include_deleted?: boolean;
}) {
  const params = new URLSearchParams();
  if (filters?.platform) params.set("platform", filters.platform);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.search) params.set("search", filters.search);
  if (filters?.include_deleted) params.set("include_deleted", "true");
  const qs = params.toString();
  return useQuery({
    queryKey: ["fleet", "avatars", filters],
    queryFn: () => api<FleetAvatar[]>(`/api/ops/v1/fleet/avatars${qs ? `?${qs}` : ""}`),
    // IR-1412 r2: background revalidation so the tab stays fresh without a
    // manual refresh; isLoading-gated UIs never flash on these refetches.
    refetchInterval: 120_000,
  });
}

/**
 * IR-1307: the canonical IRIS classification vocabulary for the avatar
 * multi-select. Served rather than hardcoded here because the list is not stable
 * (IR-1038 grew it from 7 values to 11) — a FE copy would eventually offer an
 * option the backend's serializer rejects. Cached for the session: it changes
 * only when someone edits the backend enum.
 */
export function useAvatarClassificationOptions() {
  return useQuery({
    queryKey: ["fleet", "avatars", "classification-options"],
    queryFn: () =>
      api<{ classification_options: string[] }>("/api/ops/v1/fleet/avatars/classification-options"),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useFleetAvatarDetail(id: number | null) {
  return useQuery({
    queryKey: ["fleet", "avatars", id],
    queryFn: () => api<FleetAvatar>(`/api/ops/v1/fleet/avatars/${id}`),
    enabled: id !== null,
  });
}

export function useCreateAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FleetAvatar>) =>
      api<FleetAvatar>("/api/ops/v1/fleet/avatars", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "avatars"] }),
  });
}

export function useUpdateAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: Partial<FleetAvatar> & {
      id: number;
      // status the client last saw; backend drops `status` if the row moved on.
      // Widened to match `db_status` (IR-1688): the form seeds this from
      // `editing.db_status`, which can genuinely be "ready_to_use" (IR-1516) —
      // a value the canonical `status` above never carries.
      status_baseline?: FleetAvatar["db_status"];
      // IR-1769 (T14, O-24): "Relink..." is a plain Disconnected, not a Stop.
      force_relink?: boolean;
    }) =>
      api<FleetAvatar>(`/api/ops/v1/fleet/avatars/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "avatars"] }),
  });
}

export function useDeleteAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/ops/v1/fleet/avatars/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "avatars"] }),
  });
}

// IR-1720: the operator "Resume collection" COMMAND — the undo of the IR-1287
// stop. A dedicated POST rather than a `status` PUT for the same reason Stop is
// a button and not a select value: it is an action with side effects on the
// collection machine (the backend writes the start request and moves the row to
// `active` + `status_source = 'resume'` in one step), not a label edit.
//
// Path is fixed by the cross-repo contract: no trailing slash, no request body.
// A refusal arrives as an `ApiError` whose `body.error` is a sentence written
// to be shown to the operator (409: the row is not a plain operator stop; 502:
// the start request could not be sent) — see `resumeErrorMessage`.
//
// The success invalidation IS the operator's confirmation: the refetched row
// carries `collection_resume_requested_at`, so its Collection cell flips to
// "Resume pending" without any toast.
export function useResumeCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<{ ok: boolean; status: string; collection_resume_requested_at: string }>(
        `/api/ops/v1/fleet/avatars/${id}/resume-collection`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "avatars"] }),
  });
}

// ── Capacity defaults (IR-1226 P1-D) ──
export function useCapacityDefaults() {
  return useQuery({
    queryKey: ["fleet", "capacity-defaults"],
    queryFn: () => api<CapacityDefault[]>("/api/ops/v1/fleet/capacity-defaults"),
  });
}

export function useUpdateCapacityDefault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CapacityDefault) =>
      api<CapacityDefault>("/api/ops/v1/fleet/capacity-defaults", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fleet", "capacity-defaults"] });
      // effective limits on the avatars list may change with a new default
      qc.invalidateQueries({ queryKey: ["fleet", "avatars"] });
    },
  });
}

// ── Avatar-Group Assignments ──
export function useAvatarGroups(filters?: { avatar_id?: number; group_entity_id?: number }) {
  const params = new URLSearchParams();
  if (filters?.avatar_id) params.set("avatar_id", String(filters.avatar_id));
  if (filters?.group_entity_id) params.set("group_entity_id", String(filters.group_entity_id));
  const qs = params.toString();
  return useQuery({
    queryKey: ["fleet", "avatar-groups", filters],
    queryFn: () => api<FleetAvatarGroup[]>(`/api/ops/v1/fleet/avatar-groups${qs ? `?${qs}` : ""}`),
  });
}

/**
 * IR-1450: the groups the PHONE said it is in — the ones we hold AND the ones
 * we do not. A separate query from `useAvatarGroups` because it is a separate
 * population served by a separate endpoint: a row here can have no entity at
 * all, so it carries no assignment id and must never be fed to
 * `useUnassignAvatar`.
 *
 * `enabled` is the point of the signature. The expander mounts one of these per
 * opened avatar, and the roster is only ever wanted for the row the operator
 * actually expanded — fetching it for the whole page would be 79 requests to
 * render nothing.
 */
export function useAvatarReportedGroups(avatarId: number | null | undefined) {
  return useQuery({
    queryKey: ["fleet", "avatar-reported-groups", avatarId],
    enabled: !!avatarId,
    queryFn: () =>
      api<FleetAvatarReportedGroup[]>(`/api/ops/v1/fleet/avatars/${avatarId}/reported-groups`),
  });
}

export function useAssignAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { avatar: number; group_entity_id: number; group_name?: string }) =>
      api<FleetAvatarGroup>("/api/ops/v1/fleet/avatar-groups", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fleet", "avatar-groups"] });
      qc.invalidateQueries({ queryKey: ["fleet", "avatars"] });
    },
  });
}

export function useUnassignAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/ops/v1/fleet/avatar-groups/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fleet", "avatar-groups"] });
      qc.invalidateQueries({ queryKey: ["fleet", "avatars"] });
    },
  });
}

// ── Blacklist ──
export function useBlacklist(filters?: { type?: string; platform?: string; search?: string }) {
  const params = new URLSearchParams();
  if (filters?.type) params.set("type", filters.type);
  if (filters?.platform) params.set("platform", filters.platform);
  if (filters?.search) params.set("search", filters.search);
  const qs = params.toString();
  return useQuery({
    queryKey: ["fleet", "blacklist", filters],
    queryFn: () => api<BlacklistEntry[]>(`/api/ops/v1/fleet/blacklist${qs ? `?${qs}` : ""}`),
    // IR-1412 r2: background revalidation (see useFleetAvatars).
    refetchInterval: 120_000,
  });
}

export function useAddBlacklistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<BlacklistEntry>) =>
      api<BlacklistEntry>("/api/ops/v1/fleet/blacklist", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "blacklist"] }),
  });
}

export function useDeleteBlacklistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/ops/v1/fleet/blacklist/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "blacklist"] }),
  });
}

// IR-1225: set blacklist coverage on specific avatars. `coverageDisabled = true`
// bypasses the blacklist for those avatars (DEV-only enforcement); PROD always
// excludes them. Invalidates both blacklist rows (coverage pill) and avatars.
export function useSetAvatarCoverage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, coverageDisabled }: { ids: number[]; coverageDisabled: boolean }) =>
      api<{ updated: number }>("/api/ops/v1/fleet/avatars/bulk-coverage", {
        method: "POST",
        body: JSON.stringify({ avatar_ids: ids, coverage_disabled: coverageDisabled }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fleet", "blacklist"] });
      qc.invalidateQueries({ queryKey: ["fleet", "avatars"] });
    },
  });
}

// IR-1225: set blacklist coverage on ALL avatars (excludes deleted server-side).
// Same DEV-only semantics as useSetAvatarCoverage.
export function useSetAllAvatarCoverage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ coverageDisabled }: { coverageDisabled: boolean }) =>
      api<{ updated: number }>("/api/ops/v1/fleet/avatars/bulk-coverage", {
        method: "POST",
        body: JSON.stringify({ all: true, coverage_disabled: coverageDisabled }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fleet", "blacklist"] });
      qc.invalidateQueries({ queryKey: ["fleet", "avatars"] });
    },
  });
}

// ── Join Commands ──
export function useRequestJoin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { avatar_id: number; group_entity_id: number; join_link: string }) =>
      api<JoinCommand>("/api/ops/v1/fleet/join-commands", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet"] }),
  });
}

// ── Fleet Stats ──
export function useFleetStats() {
  return useQuery({
    queryKey: ["fleet", "stats"],
    queryFn: () => api<FleetStats>("/api/ops/v1/fleet/stats"),
    refetchInterval: 60_000,
  });
}

// ── Unified Groups ──
export function useUnifiedGroups(filters: {
  page?: number;
  page_size?: number;
  search?: string;
  platform?: string;
  status?: string;
  sort?: string;
  added_within?: string;
  // IR-1147 reopen: custom date range on the Added/Created date + per-column
  // Avatar filter (comma-separated avatar names).
  added_after?: string;
  added_before?: string;
  avatar?: string;
  // IR-1227 (Rotem 53724): group collection lifecycle filter (collecting|stopped).
  collection_state?: string;
  // IR-1227 (Rotem 53901): derived single-lifecycle filter
  // (active|collection_pending|collection_stopped|deleted).
  lifecycle?: string;
  // IR-1228 P5a-ii: filter by composed overlay lifecycle status (comma-separated).
  group_status?: string;
  // IR-1425 r10: the matrix Lifecycle (11 values) and Collection axis (8
  // states), comma-separated — what the Groups tab sends since r10. The two
  // keys above stay accepted (nothing else sends them).
  lcp?: string;
  collection_axis?: string;
  // IR-1426 r2 (Rotem 2026-08-24): the Reported filter — comma-separated
  // buckets out of reported|not_reported|never_reported. Server-side, because
  // the axis lives in the fleet DB while this table is paginated out of the
  // entities one; a client-side partition could only ever filter the page.
  on_phone?: string;
  // IR-1627 (Rotem c63023): the "Messages Read" filter — comma-separated
  // states out of complete|partial|unknown, same shape as on_phone above.
  messages_quality?: string;
  // IR-1697 r11: the Ingestion column filtered PER ENVIRONMENT, server-side
  // (comma lists of `ENV_INGESTION_FILTER_TOKENS`-style tokens).
  prod_ingestion?: string;
  dev_ingestion?: string;
  // IR-1228 P5 (Rotem c61508: "Historic hidden by default"). The backend hides
  // Historic unless this is sent. Measured on prod 2026-09-04: 2,176 of 4,051
  // non-deleted groups are Historic — 53.7% of the screen.
  include_historic?: string;
}) {
  const params = new URLSearchParams();
  if (filters.page) params.set("page", String(filters.page));
  if (filters.page_size) params.set("page_size", String(filters.page_size));
  if (filters.search) params.set("search", filters.search);
  if (filters.include_historic) params.set("include_historic", filters.include_historic);
  if (filters.platform) params.set("platform", filters.platform);
  if (filters.status) params.set("status", filters.status);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.added_within) params.set("added_within", filters.added_within);
  if (filters.added_after) params.set("added_after", filters.added_after);
  if (filters.added_before) params.set("added_before", filters.added_before);
  if (filters.avatar) params.set("avatar", filters.avatar);
  if (filters.collection_state) params.set("collection_state", filters.collection_state);
  if (filters.lifecycle) params.set("lifecycle", filters.lifecycle);
  if (filters.group_status) params.set("group_status", filters.group_status);
  if (filters.lcp) params.set("lcp", filters.lcp);
  if (filters.collection_axis) params.set("collection_axis", filters.collection_axis);
  if (filters.on_phone) params.set("on_phone", filters.on_phone);
  if (filters.messages_quality) params.set("messages_quality", filters.messages_quality);
  if (filters.prod_ingestion) params.set("prod_ingestion", filters.prod_ingestion);
  if (filters.dev_ingestion) params.set("dev_ingestion", filters.dev_ingestion);
  const qs = params.toString();
  return useQuery({
    queryKey: ["data", "groups-unified", filters],
    queryFn: () =>
      api<UnifiedGroupsResponse>(`/api/ops/v1/data/groups-unified${qs ? `?${qs}` : ""}`),
    // IR-1412 r2: background revalidation (see useFleetAvatars).
    refetchInterval: 120_000,
  });
}

export function useUpdateJoinLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scrappingId, joinLink }: { scrappingId: string; joinLink: string }) =>
      api(`/api/ops/v1/data/groups/${scrappingId}/join-link`, {
        method: "PUT",
        body: JSON.stringify({ join_link: joinLink }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "groups-unified"] }),
  });
}

export type GroupLifecycleAction = "approve" | "not_relevant" | "set_inactive";

export function useGroupLifecycleAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: GroupLifecycleAction }) =>
      // Omit target_env → backend defaults to both envs (matches the dual-env delete).
      api(`/api/ops/v1/groups/${id}/lifecycle-action`, {
        method: "POST",
        body: JSON.stringify({ action }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "groups-unified"] }),
  });
}

export function useSetGroupNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      api(`/api/ops/v1/groups/${id}/notes`, { method: "PUT", body: JSON.stringify({ notes }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "groups-unified"] }),
  });
}

// IR-1045 P4b: "Remove Group" — terminate coverage (status-only). Writes the
// sticky coverage_terminated status on the group's fleet row; does NOT
// unassign the avatar. `id` is the encrypted entity id from the row.
export function useTerminateCoverage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ coverage_status: string }>(`/api/ops/v1/data/groups/${id}/terminate-coverage`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "groups-unified"] }),
  });
}

// ── IR-1548: group session dashboard ──

export function useGroupDashboard(encryptedId: string) {
  return useQuery({
    queryKey: ["group-dashboard", encryptedId],
    queryFn: () => api<GroupDashboardResponse>(`/api/ops/v1/data/groups/${encryptedId}/dashboard`),
    enabled: !!encryptedId,
  });
}

/**
 * `env` defaults to "both" — the backend returns ONE correctly-paginated
 * interleaved page. Never fetch prod and dev separately and concatenate:
 * two independently-paginated lists silently drop and duplicate rows.
 */
export function useGroupSessions(
  encryptedId: string,
  env: "prod" | "dev" | "both" = "both",
  page = 0,
  limit = 50,
) {
  const offset = page * limit;
  return useQuery({
    queryKey: ["group-sessions", encryptedId, env, offset, limit],
    queryFn: () =>
      api<GroupSessionsResponse>(
        `/api/ops/v1/data/groups/${encryptedId}/sessions?env=${env}&limit=${limit}&offset=${offset}`,
      ),
    enabled: !!encryptedId,
  });
}

/**
 * IR-1548: the statistics band's dataset — EVERY session for this group.
 *
 * Always `both`, deliberately, and NOT the table's env filter: the band's
 * whole subject is the difference between the environments, so filtering it
 * would leave the divergence headline with nothing to compare. The band says
 * on screen that it is unfiltered rather than silently contradicting the rows
 * beneath it.
 *
 * Separate from `useGroupSessions` rather than a bigger `limit` on it: that
 * one is paginated and carries ten fields per row, and charting whichever
 * fifty happen to be on screen while captioning it as the history is the
 * quiet wrong answer this endpoint exists to avoid.
 */
export function useSessionsOverview(encryptedId: string) {
  return useQuery({
    queryKey: ["sessions-overview", encryptedId],
    queryFn: () =>
      api<SessionsOverviewResponse>(
        `/api/ops/v1/data/groups/${encryptedId}/sessions/overview?env=both`,
      ),
    enabled: !!encryptedId,
  });
}

/**
 * The drill-down. Only enabled once a row is actually expanded — this is the
 * one call in the feature that touches GCS, and it must never run on page load.
 */
export function useSessionObjects(encryptedId: string, cycleId: string | null, env: string) {
  return useQuery({
    queryKey: ["session-objects", encryptedId, cycleId, env],
    queryFn: () =>
      api<SessionObjectsResponse>(
        `/api/ops/v1/data/groups/${encryptedId}/sessions/${encodeURIComponent(
          cycleId ?? "",
        )}/objects?env=${env}`,
      ),
    enabled: !!encryptedId && !!cycleId,
    retry: false,
  });
}
